import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Heart, Gift, Link2, Building2, ExternalLink, Sparkles, Ticket } from "lucide-react";

/**
 * Optional OnlyBest sections rendered on a public BusinessProfilePage when
 * `business.onlybest_enabled === true`. Works for businesses, agents, AND
 * individual creators because they share the same data model.
 *
 * Sections are rendered only if they have content, so the page stays clean.
 */
export default function OnlyBestSections({ business }) {
  if (!business || !business.onlybest_enabled) return null;

  const picks = Array.isArray(business.onlybest_picks) ? business.onlybest_picks : [];
  const recommendations = Array.isArray(business.onlybest_recommendations) ? business.onlybest_recommendations : [];
  const causes = Array.isArray(business.onlybest_causes) ? business.onlybest_causes : [];
  const offers = Array.isArray(business.onlybest_offers) ? business.onlybest_offers : [];
  const affiliateLinks = Array.isArray(business.onlybest_affiliate_links) ? business.onlybest_affiliate_links : [];
  const hasAnyContent = picks.length + recommendations.length + causes.length + offers.length + affiliateLinks.length > 0;

  if (!hasAnyContent && !business.onlybest_tagline) return null;

  const isCreator = business.listing_kind === "creator";
  const isAgent = business.is_individual === true || /agent|realtor|broker|loan|consultant|advisor|insurance/i.test(business.category_name || "");
  const ownerLabel = isCreator ? "Creator" : isAgent ? "Agent" : "Business";

  return (
    <section className="mt-10 space-y-8" data-testid="onlybest-public-sections">
      {/* Banner */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative overflow-hidden rounded-2xl border border-[#FBBF24]/30 bg-gradient-to-r from-[#FBBF24]/15 via-[#0F172A] to-[#EC4899]/15 p-5 sm:p-6"
        data-testid="onlybest-banner"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <span className="inline-flex w-10 h-10 rounded-xl bg-gradient-to-br from-[#FBBF24] to-[#EC4899] items-center justify-center flex-shrink-0">
            <Crown className="w-5 h-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FBBF24]">OnlyBest™ Campaign</p>
            <h2 className="text-lg sm:text-xl font-bold text-white font-['Outfit']">Recommended by this {ownerLabel}</h2>
            {business.onlybest_tagline && (
              <p className="text-sm text-[#CBD5E1] mt-1.5">"{business.onlybest_tagline}"</p>
            )}
          </div>
        </div>
      </motion.div>

      {/* Best of Best Picks */}
      {picks.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6" data-testid="onlybest-picks-section">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#FBBF24]" />
            <h3 className="text-base sm:text-lg font-semibold text-white">My Best of Best Picks</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {picks.map((p) => (
              <Link
                key={p.slug}
                to={`/business/${p.slug}`}
                className="flex items-start gap-3 p-3 rounded-xl border border-white/10 bg-[#0F172A] hover:border-[#FBBF24]/40 hover:bg-[#0F172A]/60 transition min-w-0"
                data-testid={`onlybest-pick-${p.slug}`}
              >
                {p.logo_url ? (
                  <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-lg object-cover bg-white/10 flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#94A3B8]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">{p.name}</p>
                  {(p.city || p.state) && (
                    <p className="text-[#64748B] text-xs truncate">{[p.city, p.state].filter(Boolean).join(", ")}</p>
                  )}
                  {p.note && <p className="text-[#94A3B8] text-xs mt-1 line-clamp-2">{p.note}</p>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations (external) */}
      {recommendations.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6" data-testid="onlybest-recommendations-section">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-4 h-4 text-[#3B82F6]" />
            <h3 className="text-base sm:text-lg font-semibold text-white">{isAgent ? "Trusted Vendors & Partners" : "Businesses I Recommend"}</h3>
          </div>
          <ul className="divide-y divide-white/5">
            {recommendations.map((r, i) => (
              <li key={i} className="py-3 flex items-start gap-3 min-w-0">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm">{r.name || "Recommendation"}</p>
                  {r.description && <p className="text-[#94A3B8] text-xs mt-0.5">{r.description}</p>}
                </div>
                {r.url && (
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-1 text-xs text-[#FBBF24] hover:text-white flex-shrink-0"
                    data-testid={`onlybest-rec-link-${i}`}
                  >
                    Visit <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Causes */}
      {causes.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6" data-testid="onlybest-causes-section">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-4 h-4 text-[#EC4899]" />
            <h3 className="text-base sm:text-lg font-semibold text-white">Causes I Support</h3>
          </div>
          <ul className="space-y-3">
            {causes.map((c, i) => (
              <li key={i} className="rounded-xl border border-white/10 bg-[#0F172A] p-3 min-w-0">
                <div className="flex items-start gap-3 flex-wrap">
                  <Heart className="w-4 h-4 text-[#EC4899] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">{c.name || "Cause"}</p>
                    {c.description && <p className="text-[#94A3B8] text-xs mt-0.5">{c.description}</p>}
                  </div>
                  {c.url && (
                    <a href={c.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-xs text-[#EC4899] hover:text-white flex-shrink-0" data-testid={`onlybest-cause-link-${i}`}>
                      Learn more <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Partner Offers */}
      {offers.length > 0 && (
        <div className="rounded-2xl border border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/5 to-transparent p-5 sm:p-6" data-testid="onlybest-offers-section">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-4 h-4 text-[#10B981]" />
            <h3 className="text-base sm:text-lg font-semibold text-white">Partner Offers I Share</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {offers.map((o, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-[#0F172A] p-4 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Ticket className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                  <p className="text-white font-semibold text-sm">{o.title || "Offer"}</p>
                </div>
                {o.description && <p className="text-[#94A3B8] text-xs mb-2">{o.description}</p>}
                {o.code && (
                  <p className="text-xs text-[#FBBF24] mb-2">Code: <span className="font-mono px-2 py-0.5 rounded bg-[#FBBF24]/10">{o.code}</span></p>
                )}
                {o.url && (
                  <a
                    href={o.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#10B981] hover:text-white"
                    data-testid={`onlybest-offer-link-${i}`}
                  >
                    Redeem offer <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Affiliate / referral links */}
      {affiliateLinks.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6" data-testid="onlybest-affiliate-section">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 text-[#A78BFA]" />
            <h3 className="text-base sm:text-lg font-semibold text-white">My Affiliate Picks</h3>
          </div>
          <ul className="divide-y divide-white/5">
            {affiliateLinks.map((a, i) => (
              <li key={i} className="py-3 flex items-start gap-3 min-w-0">
                <Link2 className="w-4 h-4 text-[#A78BFA] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm">{a.label || "Link"}</p>
                  {a.description && <p className="text-[#94A3B8] text-xs mt-0.5">{a.description}</p>}
                </div>
                {a.url && (
                  <a href={a.url} target="_blank" rel="noopener noreferrer nofollow sponsored" className="inline-flex items-center gap-1 text-xs text-[#A78BFA] hover:text-white flex-shrink-0" data-testid={`onlybest-aff-link-${i}`}>
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-[#475569] mt-3">Some links may be affiliate links — this {ownerLabel.toLowerCase()} may earn a commission.</p>
        </div>
      )}
    </section>
  );
}
